import { memo, useState } from 'preact/compat'
import { packaging } from '../../models/packages'
import { TextButton } from '../text-button/text-button'
import type { Product } from '../types'
import * as styles from './dispatch-form.module.css'
import type { DispatchTransaction } from './dispatch-form-shared'

interface ProductTileProps {
  product: Product
  transaction: DispatchTransaction
  onUpdate: (transaction: DispatchTransaction) => void
}

function StepperButton({
  children,
  onClick,
  disabled
}: {
  children: preact.JSX.Element
  onClick: () => void
  disabled?: boolean
}) {
  const [touched, setTouched] = useState(false)
  return (
    <button
      class={touched ? styles.stepperButtonTouched : styles.stepperButton}
      type='button'
      disabled={disabled}
      onClick={onClick}
      onTouchStart={() => setTouched(true)}
      onTouchEnd={() => setTouched(false)}
    >
      {children}
    </button>
  )
}

function ProductTileClearButton({
  transaction,
  onUpdate
}: {
  transaction: DispatchTransaction
  onUpdate: (transaction: DispatchTransaction) => void
}) {
  return (
    <div class={styles.clearButtonContainer}>
      <TextButton
        variant='error'
        onClick={() =>
          onUpdate({ ...transaction, inputPackages: 0, inputUnits: 0 })
        }
        aria-label='Produkt entfernen'
      >
        <i class='icon ion-close-round' />
      </TextButton>
    </div>
  )
}

export const ProductTileMemo = memo(function ProductTile({
  product,
  transaction = { productId: product.id, inputPackages: 0, inputUnits: 0 },
  onUpdate
}: ProductTileProps) {
  const hasInput = transaction.inputPackages > 0 || transaction.inputUnits > 0

  return (
    <div
      class={hasInput ? styles.productTileWithInput : styles.productTile}
      style={{
        '--category-color': product.color
      }}
    >
      <div class={styles.productName}>
        <div class={styles.productNameText}>{product.name}</div>

        {hasInput && (
          <ProductTileClearButton
            transaction={transaction}
            onUpdate={onUpdate}
          />
        )}
      </div>

      <div class={styles.inputRow}>
        <StepperButton
          disabled={transaction.inputPackages === 0}
          onClick={() => {
            onUpdate({
              ...transaction,
              inputPackages: Math.max(0, transaction.inputPackages - 1)
            })
          }}
        >
          <i class='icon ion-minus-round' />
        </StepperButton>
        <label>
          <input
            class='pure-input-1'
            name='inputPackages'
            type='text'
            inputMode='numeric'
            placeholder='0'
            value={transaction.inputPackages || ''}
            onChange={(event) => {
              let inputPackages = Number.parseInt(event.currentTarget.value)
              if (inputPackages < 0) {
                inputPackages = 0
              }

              if (Number.isNaN(inputPackages)) {
                inputPackages = transaction.inputPackages
              }

              onUpdate({ ...transaction, inputPackages })
            }}
          />

          {packaging.get(product.packageId).name}
        </label>
        <StepperButton
          onClick={() => {
            onUpdate({
              ...transaction,
              inputPackages: transaction.inputPackages + 1
            })
          }}
        >
          <i class='icon ion-plus-round' />
        </StepperButton>
      </div>
      <div class={styles.inputRow}>
        <StepperButton
          disabled={transaction.inputUnits === 0}
          onClick={() => {
            onUpdate({
              ...transaction,
              inputUnits: Math.max(0, transaction.inputUnits - 1)
            })
          }}
        >
          <i class='icon ion-minus-round' />
        </StepperButton>
        <label>
          <input
            class='pure-input-1'
            name='inputUnits'
            type='text'
            inputMode='numeric'
            placeholder='0'
            value={transaction.inputUnits || ''}
            onChange={(event) => {
              let inputUnits = Number.parseFloat(event.currentTarget.value)
              if (inputUnits < 0) {
                inputUnits = 0
              }

              if (Number.isNaN(inputUnits)) {
                inputUnits = transaction.inputUnits
              }

              onUpdate({ ...transaction, inputUnits })
            }}
          />

          {packaging.get(product.unitId).name}
        </label>
        <StepperButton
          onClick={() => {
            onUpdate({
              ...transaction,
              inputUnits: transaction.inputUnits + 1
            })
          }}
        >
          <i class='icon ion-plus-round' />
        </StepperButton>
      </div>
    </div>
  )
})
